(() => {
  'use strict';
  const states = new WeakMap(), pending = new Set(), active = new Set();
  let frame = 0, fontGeneration = 0;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  function queue(element) {
    if (!active.has(element) || !element.isConnected) return;
    pending.add(element);
    if (!frame) frame = requestAnimationFrame(flush);
  }
  const visibility = new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting) { active.add(entry.target); queue(entry.target); }
    else active.delete(entry.target);
  }), {rootMargin:'150px 0px'});
  const resize = new ResizeObserver(entries => entries.forEach(entry => {
    const state = states.get(entry.target);
    if (!state || Math.abs(entry.contentRect.width - state.width) < .5) return;
    state.width = entry.contentRect.width;
    queue(entry.target);
  }));
  function flush() {
    frame = 0;
    const jobs = [];
    pending.forEach(element => {
      const state = states.get(element);
      if (!element.isConnected || !active.has(element) || !state || state.canFit?.() === false) return;
      const width = element.getBoundingClientRect().width;
      if (!width) return;
      const style = getComputedStyle(element);
      const key = [width.toFixed(1),style.font,fontGeneration,state.link.textContent].join('|');
      if (state.key === key) return;
      jobs.push({element,state,key});
    });
    pending.clear();
    jobs.forEach(({state}) => { state.prepare(); state.text.textContent = state.full; });
    const fitted = jobs.map(job => {
      const {element,state} = job;
      const style = getComputedStyle(element), line = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.6;
      if (element.getBoundingClientRect().height <= line * state.lines + 1) return {...job,preview:state.full};
      const node = state.text.firstChild;
      if (!node || node.nodeType !== Node.TEXT_NODE) return {...job,preview:state.full};
      const range = document.createRange();
      range.selectNodeContents(state.text);
      const rects = Array.from(range.getClientRects());
      if (!rects.length) return {...job,preview:state.full};
      const top = Math.min(...rects.map(rect => rect.top)), lastTop = top + line * (state.lines - 1);
      context.font = getComputedStyle(state.text).font;
      const reserve = state.link.getBoundingClientRect().width + context.measureText('… ').width + 8;
      const right = element.getBoundingClientRect().right - parseFloat(style.paddingRight || 0) - reserve;
      const units = Array.from(state.full), offsets = [0];
      units.forEach(unit => offsets.push(offsets[offsets.length - 1] + unit.length));
      let low = 0, high = units.length;
      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        range.setStart(node, offsets[mid - 1]);
        range.setEnd(node, offsets[mid]);
        const rect = range.getBoundingClientRect();
        const fits = rect.top <= lastTop + 1 && (rect.top < lastTop - line * .5 || rect.right <= right);
        if (fits) low = mid; else high = mid - 1;
      }
      let preview = units.slice(0,low).join('').trimEnd();
      if (/[A-Za-z0-9]$/.test(preview) && /[A-Za-z0-9]/.test(units[low] || '')) preview = preview.replace(/\s+\S*$/, '');
      return {...job,preview:preview.replace(/[.,;:，。；：]+$/, '') + '…'};
    });
    fitted.forEach(({state,key,preview}) => { state.text.textContent = preview; state.key = key; });
  }
  window.AceTextPreview = {
    register(element,options) {
      if (!states.has(element)) {
        states.set(element,{...options,full:options.text.textContent.trim().replace(/\s+/g,' '),width:0});
        visibility.observe(element);
        resize.observe(element);
      }
      queue(element);
    },
    queue
  };
  document.fonts?.ready.then(() => { fontGeneration++; active.forEach(queue); });
})();

(() => {
  'use strict';
  const configuration = document.getElementById('ace-k5-ui-config');
  if (!configuration) return;
  let labelNumber = 0;
  function nameLayouts(scope) {
    scope.querySelectorAll('hdt-view-layout-switch button').forEach(button => {
      button.setAttribute('aria-pressed', String(button.classList.contains('is-active')));
    });
  }
  function nameFields(scope) {
    scope.querySelectorAll('.hdt-newsletter__email').forEach(field => {
      if (!field.hasAttribute('aria-label') && !field.hasAttribute('aria-labelledby')) field.setAttribute('aria-label', field.placeholder);
    });
    scope.querySelectorAll('.ecom__form-field').forEach(wrapper => {
      const label = wrapper.querySelector('label.ecom__form-field-label');
      const field = wrapper.querySelector('input:not([type="hidden"]), select, textarea');
      if (!label || !field || !label.textContent.trim()) return;
      if (field.id) label.htmlFor = field.id;
      if (!label.id) {
        do { label.id = `ace-k5-form-label-${++labelNumber}`; }
        while (document.getElementById(label.id) !== label);
      }
      if (!field.hasAttribute('aria-labelledby')) field.setAttribute('aria-labelledby', label.id);
      if (field.required || field.getAttribute('aria-required') === 'true') {
        field.required = true;
        field.setAttribute('aria-required', 'true');
      }
    });
  }
  function nameTables(scope) {
    scope.querySelectorAll('.ecom-html table, .ecom-html__inner table, .ecom__element--text table').forEach(table => {
      let wrapper = table.closest('.ace-k5-table-scroll');
      if (!wrapper && document.readyState === 'complete') {
        wrapper = document.createElement('div');
        table.before(wrapper); wrapper.append(table);
      }
      if (!wrapper) return;
      wrapper.classList.add('ace-k5-table-scroll');
      wrapper.tabIndex = 0; wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', table.caption?.textContent.trim() || configuration.dataset.tableLabel);
    });
  }
  function nameImageLinks(scope) {
    scope.querySelectorAll('.hdt-collection-item .hdt-full-width-link[aria-label="link"]').forEach(link => {
      const title = link.closest('.hdt-collection-item')?.querySelector('.hdt-collection-title-text')?.textContent.trim();
      if (title) link.setAttribute('aria-label', title);
    });
  }
  function rgb(value) {
    const match = value.match(/^rgba?\(\s*(\d+(?:\.\d+)?)[, ]+\s*(\d+(?:\.\d+)?)[, ]+\s*(\d+(?:\.\d+)?)(?:[, /]+\s*([\d.]+))?\s*\)$/);
    return !match || (match[4] !== undefined && Number(match[4]) < 1) ? null : match.slice(1,4).map(Number);
  }
  function luminance(colour) {
    return colour.map(channel => { const v=channel/255; return v<=0.04045?v/12.92:((v+0.055)/1.055)**2.4; }).reduce((sum,v,i)=>sum+v*[0.2126,0.7152,0.0722][i],0);
  }
  function readableButtons(scope) {
    scope.querySelectorAll('.ecom__form-button, .ecom-element-button .ecom__element--button').forEach(button => {
      if (button.classList.contains('ace-k5-readable-green')) return;
      const style = getComputedStyle(button), background=rgb(style.backgroundColor), foreground=rgb(style.color);
      if (!background || !foreground || Math.min(...foreground)<230 || background[1]<=background[0]*1.4 || background[1]<=background[2]*1.4) return;
      if ((luminance(foreground)+0.05)/(luminance(background)+0.05)<4.5) button.classList.add('ace-k5-readable-green');
    });
  }
  function toolbarSpacing() {
    const toolbar = document.querySelector('.hdt-toolbar-mobile .hdt-toolbar, .hdt-toolbar');
    const visible = toolbar && matchMedia('(max-width: 1149px)').matches && toolbar.getBoundingClientRect().height > 0 && getComputedStyle(toolbar).position === 'fixed';
    document.body.classList.toggle('ace-k5-toolbar-visible', Boolean(visible));
    if (visible) document.body.style.setProperty('--ace-k5-toolbar-height', `${Math.ceil(toolbar.getBoundingClientRect().height)}px`);
  }
  function fitCategoryFlyouts() {
    if (!matchMedia('(min-width: 1150px)').matches) return;
    document.querySelectorAll('.ace-k5-category-flyouts').forEach(menu => {
      const header = menu.closest('.hdt-section-header');
      const topLimit = Math.max(8, (header?.getBoundingClientRect().bottom || 0) + 8);
      const bottomLimit = window.innerHeight - (document.getElementById('PBarNextFrame') ? 72 : 8);
      const available = Math.max(80, bottomLimit - topLimit);
      menu.querySelectorAll('hdt-header-menu > details[open] > .hdt-sub-menu').forEach(panel => {
        const details = panel.parentElement;
        const summary = details.querySelector(':scope > summary');
        const parent = panel.offsetParent;
        if (!summary || !parent) return;
        const leaf = !panel.querySelector('hdt-header-menu');
        panel.toggleAttribute('data-ace-flyout-scroll', leaf);
        panel.style.setProperty('--ace-flyout-max-height', available + 'px');
        const desiredTop = Math.max(topLimit, Math.min(summary.getBoundingClientRect().top, bottomLimit - panel.offsetHeight));
        const parentTop = parent.getBoundingClientRect().top + parent.clientTop - parent.scrollTop;
        panel.style.setProperty('--ace-flyout-top', (desiredTop - parentTop) + 'px');
        panel.setAttribute('data-ace-flyout-fitted', '');
      });
    });
  }
  function closeMenuBranch(details) {
    const branches = [...details.querySelectorAll('details[open]'), details].reverse();
    branches.forEach(branch => {
      const summary = branch.querySelector(':scope > summary');
      if (summary?.getAttribute('aria-expanded') !== 'true') return;
      const controller = branch.parentElement;
      if (typeof controller?.toggle === 'function') controller.toggle(false);
    });
  }
  function closeSiblingMenus(details) {
    const list = details.closest('li')?.parentElement;
    if (list?.tagName !== 'UL') return;
    list.querySelectorAll(':scope > li > :is(hdt-accordion, hdt-header-menu) > details').forEach(branch => {
      if (branch !== details) closeMenuBranch(branch);
    });
  }
  document.addEventListener('click', event => {
    const summary = event.target.closest('summary');
    const details = summary?.parentElement;
    if (!details?.closest('[data-ace-single-open]')) return;
    if (details.parentElement.hasAttribute('only-click-icon') && !event.target.closest('[accordion-icon]')) return;
    if (summary.getAttribute('aria-expanded') !== 'true') closeSiblingMenus(details);
  }, true);
  document.addEventListener('toggle', event => {
    const details = event.target;
    if (details.open && details.closest?.('.ace-k5-category-flyouts')) requestAnimationFrame(fitCategoryFlyouts);
    if (!details.matches?.('details') || !details.closest('[data-ace-single-open]')) return;
    if (details.open && details.querySelector(':scope > summary')?.getAttribute('aria-expanded') === 'true') {
      closeSiblingMenus(details);
    } else if (!details.open) {
      details.querySelectorAll('details[open]').forEach(closeMenuBranch);
    }
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || !event.target.closest('[data-ace-single-open]')) return;
    const details = event.target.closest('details[open]');
    if (!details) return;
    closeMenuBranch(details);
    details.querySelector(':scope > summary')?.focus();
    event.preventDefault();
    event.stopPropagation();
  }, true);
  function syncSlideshow(slider) {
    const button = slider.querySelector?.('[data-ace-slideshow-toggle]');
    if (!button || typeof slider.aceAutoplayIsPlaying !== 'function') return;
    const playing = slider.aceAutoplayIsPlaying();
    const label = playing ? button.dataset.pauseLabel : button.dataset.playLabel;
    button.disabled = false;
    button.dataset.playing = String(playing);
    button.setAttribute('aria-label', label);
    button.title = !playing && matchMedia('(prefers-reduced-motion: reduce)').matches ? (button.dataset.motionLabel || label) : label;
    button.querySelector('span').textContent = playing ? (button.dataset.pauseText || label) : (button.dataset.playText || label);
  }
  function nameSlides(scope) {
    scope.querySelectorAll('hdt-slideshow[data-ace-autoplay-controls], hdt-slider[data-ace-autoplay-controls]').forEach(syncSlideshow);
  }
  document.addEventListener('ace:autoplay-change', event => syncSlideshow(event.target));
  document.addEventListener('init:slide', event => syncSlideshow(event.target), true);
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-ace-slideshow-toggle]');
    const slider = button?.closest('hdt-slideshow, hdt-slider');
    if (typeof slider?.aceSetAutoplay !== 'function') return;
    slider.aceSetAutoplay(!slider.aceAutoplayIsPlaying());
    syncSlideshow(slider);
  });
  const motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
  motionPreference.addEventListener('change', () => {
    if (!motionPreference.matches) return;
    document.querySelectorAll('hdt-slideshow[data-ace-autoplay-controls], hdt-slider[data-ace-autoplay-controls]').forEach(slider => slider.aceSetAutoplay?.(false));
  });
  document.addEventListener('focusin', event => {
    if (event.target.closest('[data-ace-slideshow-toggle]')) return;
    const slider = event.target.closest('hdt-slideshow[data-ace-autoplay-controls], hdt-slider[data-ace-autoplay-controls]');
    slider?.aceSetAutoplay?.(false);
  });
  const deferredTabRoots = new WeakSet();
  const deferredTabObserver = new MutationObserver(mutations => {
    mutations.forEach(({target}) => {
      if (!target.matches('[tab-item][tab-selected]')) return;
      target.querySelectorAll('hdt-slider[data-ace-start-on-approach]').forEach(slider => {
        if (slider.aceSliderStarted || slider.aceSliderStarting || typeof slider.aceStartNativeSlider !== 'function') return;
        const rect = slider.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0 && rect.top <= innerHeight + 320 && rect.bottom >= -320) slider.aceStartNativeSlider();
      });
    });
  });
  function watchDeferredCarouselTabs(scope) {
    scope.querySelectorAll('[id$="__ace_familiar_discovery"] hdt-tab[aria-controls], [id$="__ace_familiar_accessories"] hdt-tab[aria-controls]').forEach(tab => {
      const root = document.getElementById(tab.getAttribute('aria-controls'));
      if (!root || deferredTabRoots.has(root)) return;
      deferredTabObserver.observe(root, {subtree: true, attributes: true, attributeFilter: ['tab-selected']});
      deferredTabRoots.add(root);
    });
  }
  customElements.whenDefined('hdt-slideshow').then(() => nameSlides(document));
  customElements.whenDefined('hdt-slider').then(() => nameSlides(document));
  const followRoots = new WeakSet();
  const followVisibility = new IntersectionObserver(entries => entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    const root = entry.target, template = root.querySelector('template');
    followVisibility.unobserve(root);
    if (!template) return;
    const content = template.content.cloneNode(true);
    content.querySelectorAll('script').forEach(original => {
      const script = document.createElement('script');
      for (const attribute of original.attributes) script.setAttribute(attribute.name,attribute.value);
      script.textContent = original.textContent;
      original.replaceWith(script);
    });
    root.querySelector('.ace-follow-placeholder')?.remove();
    root.append(content);
    template.remove();
    shapeFollowButtons(document);
  }), {rootMargin:'400px 0px'});
  function deferFooterFollows(scope) {
    scope.querySelectorAll('[data-ace-deferred-follow]').forEach(root => {
      if (followRoots.has(root)) return;
      followRoots.add(root);
      followVisibility.observe(root);
    });
  }
  function shapeFollowButtons(scope) {
    scope.querySelectorAll('[id$="__ace_footer"] .hdt-follow-shop shop-follow-button').forEach(host => {
      customElements.whenDefined('shop-follow-button').then(() => {
        const root = host.shadowRoot;
        if (!host.isConnected || !root || root.querySelector('style[data-ace-follow-corners]')) return;
        const style = document.createElement('style');
        style.dataset.aceFollowCorners = '';
        style.textContent = 'button.group.rounded-max, button.group.rounded-max > div.rounded-max { border-radius:8px; } button.group.rounded-max { width:36px;height:36px;justify-content:center; } button.group.rounded-max > div.px-2 { padding:0; } button.group.rounded-max > span { position:absolute;width:1px;height:1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap; }';
        root.append(style);
      });
    });
  }
  // Fit the plain-text preview and its native disclosure label on three lines.
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-ace-collapse-intro]');
    const details = button?.closest('.ace-collection-intro');
    if (!details) return;
    details.open = false;
    details.querySelector('summary')?.focus({preventScroll:true});
  });
  const introStates = new WeakMap();
  function fitCollectionIntro(details) {
    const state = introStates.get(details);
    if (!state) return;
    window.AceTextPreview.register(state.summary, {
      text:state.excerpt, link:details.querySelector('.ace-collection-intro-more'), lines:3,
      prepare:() => details.classList.add('ace-intro-ready'), canFit:() => !details.open
    });
  }
  function fitCollectionIntros(scope) {
    scope.querySelectorAll('.ace-collection-intro').forEach(details => {
      if (!introStates.has(details)) {
        const excerpt = details.querySelector('.ace-collection-intro-excerpt');
        const summary = details.querySelector('summary');
        if (!excerpt || !summary) return;
        const full = excerpt.textContent.trim().replace(/\s+/g, ' ');
        introStates.set(details, { summary, excerpt, full, words: Array.from(full) });
        const panel = details.querySelector('.ace-collection-intro-full');
        const less = panel?.querySelector('[data-ace-collapse-intro]');
        if (less) {
          const walker = document.createTreeWalker(panel, NodeFilter.SHOW_TEXT);
          let lastText, node;
          while ((node = walker.nextNode())) {
            if (node.textContent.trim() && !less.contains(node) && !node.parentElement.closest('script,style')) lastText = node;
          }
          const parentLink = lastText?.parentElement.closest('a,button,h1,h2,h3,h4,h5,h6');
          const ending = parentLink && panel.contains(parentLink) ? parentLink : lastText;
          ending?.after(document.createTextNode(' '), less);
        }

        details.addEventListener('toggle', () => {
          if (details.open && document.activeElement === summary) {
            details.querySelector('[data-ace-collapse-intro]')?.focus({preventScroll:true});
          } else fitCollectionIntro(details);
        });

      }
      fitCollectionIntro(details);
    });
  }

  // Batch measurements across cards so each colour row does not force a page layout.
  const colourRows = new Set();
  const pendingColourRows = new Set();
  const colourRowWidths = new WeakMap();
  const visibleColourRows = new WeakSet();
  const colourRowVisibility = new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting) { visibleColourRows.add(entry.target); fitColourRow(entry.target); }
    else visibleColourRows.delete(entry.target);
  }), {rootMargin:"400px 0px"});
  let colourFitFrame = 0;
  function fitColourRow(option) {
    if (!option?.isConnected || !visibleColourRows.has(option)) return;
    pendingColourRows.add(option);
    if (!colourFitFrame) colourFitFrame = requestAnimationFrame(fitPendingColourRows);
  }
  function fitPendingColourRows() {
    colourFitFrame = 0;
    const rows = [];
    pendingColourRows.forEach(option => {
      const list = option.isConnected && option.querySelector('.hdt-variants-list');
      const more = list?.querySelector('.is--swatch-link');
      if (!more) return;
      const items = Array.from(list.children).filter(e => !e.classList.contains('is--swatch-link') && !e.hasAttribute('hidden'));
      rows.push({option,list,more,items});
    });
    pendingColourRows.clear();
    rows.forEach(({more,items}) => {
      items.forEach(e => e.classList.remove('is--swatch-limit'));
      more.hidden = !items.length;
    });
    const measured = rows.map(row => {
      const width = row.list.getBoundingClientRect().width;
      if (!width || !row.items.length) return {...row,width};
      return {...row,width,gap:parseFloat(getComputedStyle(row.list).columnGap)||0,itemWidth:row.items[0].getBoundingClientRect().width,moreWidth:row.more.getBoundingClientRect().width};
    });
    measured.forEach(({option,more,items,width,gap,itemWidth,moreWidth}) => {
      if (!width || !items.length) return;
      if (items.length * itemWidth + (items.length - 1) * gap <= width + 0.5) {
        more.hidden = true;
        return;
      }
      const shown = Math.max(1,Math.floor((width-moreWidth)/(itemWidth+gap)));
      items[shown]?.classList.add('is--swatch-limit');
      more.querySelector('a').textContent = '+'+(items.length-shown);
    });
  }
  const colourRowResize = new ResizeObserver(entries => entries.forEach(entry => {
    if (Math.abs(entry.contentRect.width - (colourRowWidths.get(entry.target)||0)) > 0.5) {
      colourRowWidths.set(entry.target,entry.contentRect.width);
      fitColourRow(entry.target);
    }
  }));
  const colourRowChanges = new MutationObserver(records => new Set(records.map(r => r.target)).forEach(fitColourRow));
  function fitColourRows() {
    colourRowChanges.disconnect();
    colourRows.forEach(option => {
      if (option.isConnected) return;
      colourRowResize.unobserve(option);
      colourRowVisibility.unobserve(option);
      colourRows.delete(option);
    });
    document.querySelectorAll('.ace-card-options .hdt-variant-option--color[action-go]').forEach(option => {
      if (!colourRows.has(option)) {
        colourRows.add(option);
        colourRowResize.observe(option);
        colourRowVisibility.observe(option);
      }
      colourRowChanges.observe(option, {childList:true});
    });
  }

  function fitProductExcerpts(scope) {
    scope.querySelectorAll('.ace-familiar-excerpt p').forEach(paragraph => {
      const text = paragraph.querySelector('[data-ace-product-excerpt]');
      const link = paragraph.querySelector('.ace-familiar-read-more');
      if (!text || !link) return;
      window.AceTextPreview.register(paragraph, {
        text, link, lines:3, prepare:() => paragraph.setAttribute('data-ace-excerpt-ready','')
      });
    });
  }
  const watchedCounts = new WeakSet();
  function watchCounts(scope) {
    scope.querySelectorAll('hdt-cart-count,hdt-wishlist-count,hdt-compare-count').forEach(count => {
      if (watchedCounts.has(count)) return;
      watchedCounts.add(count);
      const update = () => count.toggleAttribute('data-ace-zero', !parseInt(count.textContent,10));
      new MutationObserver(update).observe(count,{childList:true,characterData:true,subtree:true});
      update();
    });
  }
  function fitComparisonSpecs(scope) {
    scope.querySelectorAll('.ace-compare-specifications:not([data-ace-specs-ready])').forEach(row => {
      const sources = Array.from(row.querySelectorAll(':scope > .hdt-compare-value'));
      if (!sources.some(source => source.querySelector('[data-ace-compare-specs] tr'))) return;
      const features = new Map();
      const columns = sources.map(source => {
        const values = new Map();
        source.querySelectorAll('[data-ace-compare-specs] tr').forEach(tr => {
          const cells = Array.from(tr.querySelectorAll(':scope > th,:scope > td'));
          if (cells.length < 2) return;
          const label = cells[0].textContent.trim().replace(/\s+/g,' ');
          const value = cells.slice(1).map(cell => cell.textContent.trim().replace(/\s+/g,' ')).filter(Boolean).join(' | ');
          const key = label.toLocaleLowerCase().replace(/[:：\s]+/g,' ').trim();
          if (!key || !value || label.length > 100 || values.has(key) || /^(specs?|specifications?|规格|規格|spesifikasi)$/i.test(key)) return;
          values.set(key,value);
          const feature = features.get(key) || {key,label,count:0};
          feature.count++;
          features.set(key,feature);
        });
        return values;
      });
      if (!features.size) return;
      const missing = row.closest('main-compare').dataset.aceMissingSpec;
      const selected = [...features.values()].sort((a,b)=>b.count-a.count).slice(0,14);
      const fragment = document.createDocumentFragment();
      selected.forEach(feature => {
        const line = document.createElement('div');
        line.className='hdt-compare-row hdt-row-flex hdt-flex-cols-4 hdt-snap-flex ace-compare-feature';
        const label = document.createElement('div');
        label.className='hdt-compare-col hdt-col hdt-compare-field hdt-text-start';
        label.textContent=feature.label;
        line.append(label);
        sources.forEach((source,index) => {
          const cell = document.createElement('div');
          cell.className='hdt-compare-col hdt-col hdt-compare-value hdt-compare-item-'+source.dataset.productId;
          cell.textContent=columns[index].get(feature.key)||missing;
          line.append(cell);
        });
        fragment.append(line);
      });
      row.before(fragment);
      row.dataset.aceSpecsReady='';
    });
  }

  function enhance(scope=document) { fitProductExcerpts(scope); watchCounts(scope); fitComparisonSpecs(scope); fitColourRows(); fitCollectionIntros(scope); nameLayouts(scope); nameFields(scope); nameTables(scope); nameImageLinks(scope); readableButtons(scope); toolbarSpacing(); nameSlides(scope); watchDeferredCarouselTabs(scope); shapeFollowButtons(scope); deferFooterFollows(scope); }
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',()=>enhance(),{once:true}); else enhance();
  window.addEventListener('load',()=>enhance(),{once:true});
  window.addEventListener('resize',()=>{ toolbarSpacing(); fitCategoryFlyouts(); },{passive:true});
  document.addEventListener('shopify:section:load',event=>enhance(event.target));
  document.addEventListener('products:update',()=>enhance());
  document.addEventListener('click',event=>{ const button=event.target.closest('hdt-view-layout-switch button'); if(button) requestAnimationFrame(()=>nameLayouts(button.closest('hdt-view-layout-switch'))); });
})();

// Decorative loading state only: native image sources, priorities and alt text stay intact.
(() => {
  'use strict';
  if (!document.querySelector('#ace-k5-ui-config[data-ace-photo-loader]')) return;
  const photos = '.hdt-ratio > img, .hdt-ratio > picture > img';
  const observed = new WeakSet();
  const inView = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => entry.target.toggleAttribute('data-ace-photo-in-view', entry.isIntersecting));
  }, {rootMargin: '64px'}) : null;
  function refresh(frame) {
    if (!frame) return;
    if (!frame.isConnected) {
      inView?.unobserve(frame);
      observed.delete(frame);
      return;
    }
    const images = Array.from(frame.querySelectorAll(':scope > img, :scope > picture > img'));
    images.forEach(image => image.toggleAttribute('data-ace-photo-error', image.complete && image.naturalWidth === 0));
    const pending = images.some(image => !image.classList.contains('hdt-card-product__media--hover') && !image.complete);
    const hoverPending = images.some(image => image.classList.contains('hdt-card-product__media--hover') && !image.complete);
    frame.toggleAttribute('data-ace-photo-loading', pending);
    frame.toggleAttribute('data-ace-photo-hover-loading', hoverPending);
    if (pending || hoverPending) {
      if (inView && !observed.has(frame)) { inView.observe(frame); observed.add(frame); }
    } else {
      inView?.unobserve(frame);
      observed.delete(frame);
      frame.removeAttribute('data-ace-photo-in-view');
    }
    if ((pending || hoverPending) && !frame.querySelector(':scope > .ace-photo-loader')) {
      const loader = document.createElement('span');
      loader.className = 'ace-photo-loader';
      loader.setAttribute('aria-hidden', 'true');
      const trace = document.querySelector('#ace-photo-trace-template');
      if (trace instanceof HTMLTemplateElement) loader.append(trace.content.cloneNode(true));
      frame.append(loader);
    }
  }
  function collect(node, frames) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.matches(photos)) frames.add(node.closest('.hdt-ratio'));
    node.querySelectorAll(photos).forEach(image => frames.add(image.closest('.hdt-ratio')));
  }
  function settled(event) {
    if (event.target instanceof HTMLImageElement && event.target.matches(photos)) refresh(event.target.closest('.hdt-ratio'));
  }
  document.addEventListener('load', settled, true);
  document.addEventListener('error', settled, true);
  const initial = new Set();
  collect(document.documentElement, initial);
  initial.forEach(refresh);
  new MutationObserver(records => {
    const frames = new Set();
    records.forEach(record => {
      if (record.type === 'attributes') {
        if (record.target.matches(photos)) frames.add(record.target.closest('.hdt-ratio'));
      } else {
        if (record.target.matches('.hdt-ratio')) frames.add(record.target);
        record.addedNodes.forEach(node => collect(node, frames));
        record.removedNodes.forEach(node => collect(node, frames));
      }
    });
    frames.forEach(refresh);
  }).observe(document.documentElement, {subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset']});
})();


/* One-time service-outline transfer. Never submits a form or replaces a visitor's message. */
(() => {
  'use strict';
  const storageKey = 'ace.service.enquiry.v1';
  const kinds = ['trade', 'training', 'poc'];
  const ttl = 15 * 60 * 1000;
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[data-ace-service-brief]');
    if (!link || !kinds.includes(link.dataset.aceServiceKind)) return;
    try {
      const destination = new URL(link.href, location.href);
      if (destination.origin !== location.origin || !/\/pages\/contact-us\/?$/.test(destination.pathname)) return;
      const brief = link.dataset.aceServiceBrief;
      if (!brief || brief.length > 6000) return;
      sessionStorage.setItem(storageKey, JSON.stringify({version: 1, kind: link.dataset.aceServiceKind, createdAt: Date.now(), brief}));
    } catch { /* The contact link remains usable when browser storage is unavailable. */ }
  });
  const url = new URL(location.href);
  const kind = url.searchParams.get('service_enquiry');
  if (!kinds.includes(kind) || !/^\/(?:[a-z]{2,3}(?:-[a-z]{2})?\/)?pages\/contact-us\/?$/i.test(url.pathname)) return;
  const field = document.querySelector('form.ace-contact__form textarea[name="contact[message]"]');
  if (!field) return;
  try {
    const raw = sessionStorage.getItem(storageKey);
    sessionStorage.removeItem(storageKey);
    if (!raw || raw.length > 16000 || field.value !== '' || field.defaultValue !== '') return;
    const value = JSON.parse(raw);
    if (value.version !== 1 || value.kind !== kind || !Number.isInteger(value.createdAt) || Date.now() < value.createdAt || Date.now() - value.createdAt > ttl) return;
    if (typeof value.brief !== 'string' || !value.brief.trim() || value.brief.length > 6000 || /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(value.brief)) return;
    field.value = value.brief;
    field.dispatchEvent(new Event('input', {bubbles: true}));
    field.dispatchEvent(new Event('change', {bubbles: true}));
  } catch { /* No existing enquiry data is changed. */ }
  finally {
    url.searchParams.delete('service_enquiry');
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  }
})();
